// App chrome: dark header (house pattern), role-adaptive navigation — bottom
// tab bar below 768px, left sidebar above. One nav config filtered by can().
import { useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../auth/AuthProvider'
import type { Capability } from '../lib/types'
import { fetchCounters } from '../api/reads'
import { CountBadge, Modal } from '../components/ui'
import { todayLocal, weekKeyFor } from '../lib/time'

interface NavItem {
  to: string
  label: string
  icon: string
  capability: Capability | null
  badge?: boolean
}

const PRIMARY_EMPLOYEE: NavItem[] = [
  { to: '/', label: 'My Shifts', icon: '🕐', capability: null },
  { to: '/team', label: 'Team', icon: '👥', capability: 'view_team' },
  { to: '/open', label: 'Open', icon: '📣', capability: 'claim_open' },
  { to: '/requests', label: 'Requests', icon: '↔️', capability: 'request_shift_change' },
  { to: '/profile', label: 'Profile', icon: '👤', capability: null },
]

const PRIMARY_SCHEDULER: NavItem[] = [
  { to: '/', label: 'My Shifts', icon: '🕐', capability: null },
  { to: '/schedule', label: 'Schedule', icon: '📅', capability: 'build_schedule' },
  { to: '/approvals', label: 'Approvals', icon: '✅', capability: 'approve_requests', badge: true },
  { to: '/open', label: 'Open', icon: '📣', capability: 'claim_open' },
]

const MORE_ITEMS: NavItem[] = [
  { to: '/team', label: 'Team schedule', icon: '👥', capability: 'view_team' },
  { to: '/requests', label: 'My requests', icon: '↔️', capability: 'request_shift_change' },
  { to: '/availability', label: 'Availability', icon: '🗓️', capability: 'edit_availability' },
  { to: '/roster', label: 'Roster', icon: '📇', capability: 'build_schedule' },
  { to: '/reports', label: 'Reports', icon: '📊', capability: 'approve_requests' },
  { to: '/credentials', label: 'Credentials', icon: '🎖️', capability: 'manage_credentials' },
  { to: '/templates', label: 'Templates', icon: '📋', capability: 'manage_templates' },
  { to: '/settings', label: 'Department settings', icon: '⚙️', capability: 'manage_department' },
  { to: '/admin', label: 'Departments', icon: '🏛️', capability: 'manage_departments' },
  { to: '/admin/users', label: 'All users', icon: '🌐', capability: 'manage_global' },
  { to: '/admin/audit', label: 'Audit log', icon: '🔍', capability: 'view_audit' },
  { to: '/profile', label: 'Profile', icon: '👤', capability: null },
]

export function AppShell() {
  const { can, employee, signOutNow } = useAuth()
  const navigate = useNavigate()
  const [moreOpen, setMoreOpen] = useState(false)
  const isScheduler = can('build_schedule')

  const { data: counters } = useQuery({
    queryKey: ['counters', employee?.departmentId],
    queryFn: () => fetchCounters(employee!.departmentId!),
    enabled: isScheduler && !!employee?.departmentId,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  })
  const pendingCount = counters
    ? counters.swaps + counters.drops + counters.claims + counters.timeoff + (can('approve_ot') ? counters.overtime : 0)
    : 0

  const primary = (isScheduler ? PRIMARY_SCHEDULER : PRIMARY_EMPLOYEE).filter(
    (i) => i.capability === null || can(i.capability),
  )
  const more = MORE_ITEMS.filter(
    (i) => (i.capability === null || can(i.capability)) && !primary.some((p) => p.to === i.to),
  )

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-md px-3 py-2.5 ${isActive ? 'bg-sky/30 font-bold text-interlochen' : 'text-ink hover:bg-sky/15'}`

  const resolveTo = (to: string) => (to === '/schedule' ? `/schedule/${weekKeyFor(todayLocal())}` : to)

  return (
    <div className="flex min-h-dvh flex-col md:flex-row">
      {/* Sidebar ≥768px */}
      <aside className="no-print hidden w-60 shrink-0 flex-col border-r border-hairline bg-white md:flex">
        <Header employeeName={employee?.workspace.displayName ?? ''} />
        <nav className="flex-1 space-y-0.5 p-3" aria-label="Main">
          {[...primary, ...more].map((item) => (
            <NavLink key={item.to} to={resolveTo(item.to)} end={item.to === '/'} className={navClass}>
              <span aria-hidden>{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.badge && <CountBadge count={pendingCount} />}
            </NavLink>
          ))}
        </nav>
        <button className="btn-ghost m-3" onClick={() => void signOutNow()}>
          Sign out
        </button>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile header */}
        <div className="md:hidden">
          <Header employeeName={employee?.workspace.displayName ?? ''} compact />
        </div>
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 pb-24 pt-4 md:pb-8 md:px-6">
          <Outlet />
        </main>
      </div>

      {/* Bottom tabs <768px */}
      <nav
        className="no-print fixed inset-x-0 bottom-0 z-40 flex border-t border-hairline bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label="Main"
      >
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={resolveTo(item.to)}
            end={item.to === '/'}
            className={({ isActive }) =>
              `relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] ${
                isActive ? 'font-bold text-interlochen' : 'text-stone'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <span className="absolute inset-x-3 top-0 h-0.5 rounded-full bg-sunset" aria-hidden />}
                <span className="relative text-lg" aria-hidden>
                  {item.icon}
                  {item.badge && pendingCount > 0 && (
                    <span className="absolute -right-3 -top-1">
                      <CountBadge count={pendingCount} />
                    </span>
                  )}
                </span>
                {item.label}
              </>
            )}
          </NavLink>
        ))}
        {more.length > 0 && (
          <button
            className="flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] text-stone"
            onClick={() => setMoreOpen(true)}
          >
            <span className="text-lg" aria-hidden>
              ☰
            </span>
            More
          </button>
        )}
      </nav>

      <Modal open={moreOpen} onClose={() => setMoreOpen(false)} title="More">
        <div className="grid gap-1">
          {more.map((item) => (
            <button
              key={item.to}
              className="flex items-center gap-3 rounded-md px-3 py-3 text-left hover:bg-sky/15"
              onClick={() => {
                setMoreOpen(false)
                void navigate(resolveTo(item.to))
              }}
            >
              <span aria-hidden>{item.icon}</span>
              {item.label}
            </button>
          ))}
          <button
            className="mt-2 flex items-center gap-3 rounded-md px-3 py-3 text-left text-danger hover:bg-danger/10"
            onClick={() => void signOutNow()}
          >
            Sign out
          </button>
        </div>
      </Modal>
    </div>
  )
}

function Header({ employeeName, compact = false }: { employeeName: string; compact?: boolean }) {
  return (
    <header className={`bg-interlochen text-white ${compact ? 'px-4 py-3' : 'px-5 py-4'}`}>
      <p className="text-[11px] uppercase tracking-[0.25em] text-sunbeam">Interlochen</p>
      <div className="flex items-baseline justify-between">
        <p className="text-lg font-bold">Callboard</p>
        {compact && <p className="truncate text-xs text-sky">{employeeName}</p>}
      </div>
      {!compact && <p className="truncate text-xs text-sky">{employeeName}</p>}
    </header>
  )
}
